import React from "react";
import ReactModal from "react-modal";
import CloseIcon from "@mui/icons-material/Close";
import SLMetricsTicketTimer from "./SLMetricsTicketTimer";
import "./TicketSLMetricsModal.scss";

interface SLAInfo {
  escalation_level: string;
  sla_started_at: string;
  sla_scheduled_end_at: string | null;
  sla_actual_breach_at: string | null;
  sla_duration_ms: number;
  is_resolved: boolean;
  resolved_at: string | null;
  resolution_method: string | null;
  first_response_at: string | null;
  status_at_breach: string;
  sla_emails: string[];
  is_non_actionable?: boolean;
  non_actionable_marked_at?: string | null;
  sla_type?: string | null;
}

interface TicketSLMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketData: {
    sla_breach_logs: SLAInfo[];
    status_type: string;
    id: string;
    priority?: string;
    created?: string;
  };
}

const TicketSLMetricsModal: React.FC<TicketSLMetricsModalProps> = ({
  isOpen,
  onClose,
  ticketData,
}) => {
  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "700px",
      maxHeight: "85vh",
      padding: "0",
      border: "none",
      borderRadius: "12px",
      overflow: "auto",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1000,
    },
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusBadge = (sla: SLAInfo) => {
    if (sla.is_resolved) {
      return <span className="status-badge resolved">Resolved</span>;
    }

    if (sla.is_non_actionable) {
      return (
        <span className="status-badge non-actionable">
          Marked as non-actionable
        </span>
      );
    }

    const now = new Date().getTime();
    const scheduledEnd = sla.sla_scheduled_end_at 
      ? new Date(sla.sla_scheduled_end_at).getTime() 
      : null;

    if (sla.sla_actual_breach_at || (scheduledEnd && now > scheduledEnd)) {
      return <span className="status-badge breached">Breached</span>;
    }

    return <span className="status-badge active">Active</span>;
  };

  const formatDuration = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customModalStyles}
      contentLabel="Ticket SL Matrix"
      shouldCloseOnOverlayClick={true}
    >
      <div className="ticket-sl-metrics-modal">
        <div className="modal-header">
          <div className="header-content">
            <h2>Ticket SL Matrix</h2>
            {ticketData.id && (
              <div className="ticket-info">
                <span className="ticket-id">Ticket ID: {ticketData.id}</span>
              </div>
            )}
          </div>
          <button className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          {/* Current Timer Display */}
          {ticketData.sla_breach_logs?.length > 0 && (
            <div className="current-timer-section">
              <h3>Current Ticket Resolution SL</h3>
              <div className="timer-display">
                <SLMetricsTicketTimer
                  tagBasedSlBreach={ticketData.sla_breach_logs}
                  statusType={ticketData.status_type}
                  otherMailDetails={ticketData}
                />
              </div>
            </div>
          )}

          {/* SLA History */}
          <div className="sla-history-section">
            <h3>SL history</h3>
            {ticketData.sla_breach_logs?.length > 0 ? (
              <div className="sla-list">
                {ticketData.sla_breach_logs.map((sla, index) => (
                  <div key={index} className="sla-item">
                    <div className="sla-header">
                      <div className="sla-level">
                        <span className="label">Escalation level:</span>
                        <span className="value badge-primary">
                          {sla.escalation_level}
                        </span>
                      </div>
                      {getStatusBadge(sla)}
                    </div>

                    <div className="sla-details">
                      <div className="detail-row">
                        <span className="label">Started at:</span>
                        <span className="value">
                          {formatDate(sla.sla_started_at)}
                        </span>
                      </div>

                      {sla.sla_scheduled_end_at && (
                        <div className="detail-row">
                          <span className="label">SL deadline:</span>
                          <span className="value">
                            {formatDate(sla.sla_scheduled_end_at)}
                          </span>
                        </div>
                      )}

                      {/* {sla.sla_actual_breach_at && (
                        <div className="detail-row highlight-breach">
                          <span className="label">Actual breach:</span>
                          <span className="value">
                            {formatDate(sla.sla_actual_breach_at)}
                          </span>
                        </div>
                      )} */}

                      {sla.is_non_actionable && sla.non_actionable_marked_at && (
                        <div className="detail-row highlight-non-actionable">
                          <span className="label">Marked non-actionable at:</span>
                          <span className="value">
                            {formatDate(sla.non_actionable_marked_at)}
                          </span>
                        </div>
                      )}

                      <div className="detail-row">
                        <span className="label">SL duration:</span>
                        <span className="value">
                          {formatDuration(sla.sla_duration_ms)}
                        </span>
                      </div>

                      {sla.is_resolved && (
                        <>
                          {sla.resolved_at && (
                            <div className="detail-row highlight-resolved">
                              <span className="label">Resolved at:</span>
                              <span className="value">
                                {formatDate(sla.resolved_at)}
                              </span>
                            </div>
                          )}
                          {sla.resolution_method && (
                            <div className="detail-row">
                              <span className="label">Resolution method:</span>
                              <span className="value">
                                {sla.resolution_method}
                              </span>
                            </div>
                          )}
                          {sla.first_response_at && (
                            <div className="detail-row">
                              <span className="label">First response at:</span>
                              <span className="value">
                                {formatDate(sla.first_response_at)}
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      {sla.status_at_breach && (
                        <div className="detail-row">
                          <span className="label">Status at breach:</span>
                          <span className="value" style={{ textTransform: "capitalize" }}>
                            {sla.status_at_breach}
                          </span>
                        </div>
                      )}

                      {sla.sla_emails && sla.sla_emails.length > 0 && (
                        <div className="detail-row">
                          <span className="label">SL notification emails:</span>
                          <span className="value">
                            {sla.sla_emails.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <p>No SL information available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

export default TicketSLMetricsModal;